/**
 * The wizard's wiring: the draft, the step, the equipment the tiles are drawn from, and the save.
 *
 * Decides nothing. `model/exerciseDraft` owns every change to the movement,
 * `model/draftValidation` owns what blocks a save, `model/createRequest` owns what goes on the
 * wire, and `api/exerciseMutations` makes the call and drops the catalogue.
 *
 * ── The equipment tiles are the TAXONOMY, `GET /equipment` ─────────────────────────────────
 * Every piece there is, most-used first — the canon's order — and not the catalogue's facet, which
 * only knows pieces some movement already names. It is the wizard's one read, so the equipment box
 * has its own wait, failure and emptiness (`model/equipmentTiles`); the rest of the wizard opens
 * complete.
 *
 * ── The draft is created on OPEN and thrown away on close ───────────────────────────────────
 * `ui/CreateExerciseDialog` keys this hook's host by the mode and the source id, so opening the
 * wizard on a second movement cannot inherit the first one's answers. There is nothing to reconcile
 * because there is no draft alive while the catalogue can change under it.
 *
 * ── „Dalej” refuses while the open step is not done (D19, 18 September 2026) ─────────────────
 * Each step owns its blockers (`stepBlockers`): the name and the metrics rule on step 1, the
 * primary muscle on step 2, a refused link on step 3. „Dalej” and a rail jump forward stop at the
 * first step that is not done and send focus to the field that fixes it; the save asks every step.
 * An earlier revision let „Dalej” through and refused only at the save — the owner overturned it.
 *
 * ── A save waits for a cover that is still being prepared ──────────────────────────────────
 * A chosen picture is re-encoded (`~/shared/media/compressCover`) before it becomes the draft's
 * cover, and the preparation lives HERE rather than in the drop zone, because the save needs it. A
 * save pressed inside that beat waits for the preparation that was running when it was pressed
 * (`saveMove`), the footer says so, and it sends the picture it produces. One that ends in a refusal
 * does not save: the refusal is said, and the next press saves without that picture. An earlier
 * revision prepared in the zone and saved without the picture, silently.
 *
 * ── The FILM is prepared the same way, and can be cancelled ─────────────────────────────────
 * A chosen film is compressed in a worker (`~/shared/media/video/prepareVideo`) — seconds to a
 * minute — with its progress in the zone, and a save pressed meanwhile waits for it exactly as it
 * waits for a cover. Replacing or removing the film, or closing the wizard, ABORTS the preparation,
 * which terminates the worker; an encoder nobody waits for would otherwise run to the end. Choosing
 * a new film drops the one already chosen at once, so the zone never names a film that will not be
 * sent: what it shows is the new one, its refusal (naming it), or nothing.
 *
 * ── Nothing the save carries can change while it runs ───────────────────────────────────────
 * The save sends the draft as it stood at the press, and waits for the film that was being prepared
 * then. So while it runs the rail and the step body are `inert` (`ui/CreateExerciseDialog`) and the
 * four media handlers refuse: an edit made then would be silently lost, and a film removed or
 * replaced then would abort the very preparation the save awaits. A preparation that rejects under
 * a running save therefore means the wizard itself went away, and the save fails rather than going
 * without the film.
 *
 * ── Two parts of a save may be walked away from ─────────────────────────────────────────────
 * A save pressed while a film is still being compressed may wait up to a minute before anything is
 * written. „Anuluj” is „Przerwij zapis” then (`stopSave`): the WAIT ends, nothing is sent, the draft
 * stays, and the compression carries on, so the next press saves with the film. After the movement
 * (and its cover) are saved the film goes up (slot → PUT → confirm) and „Anuluj” is „Przerwij
 * wysyłanie” (`stopVideoUpload`): the PUT is aborted, the save resolves as saved with the film
 * `stopped`, and the wizard closes onto the card like any other save. Once the PUT has answered the
 * confirm is not stoppable (`api/exerciseMutations`), and the control is gone. The write between
 * the two cannot be stopped at all — `model/saveProgress` says which of these the control is.
 *
 * ── The wizard going away stops what it started ─────────────────────────────────────────────
 * Unmounting (the dialog closing is refused while a save runs, but leaving the PAGE is not) aborts a
 * compression, a save's wait and a film's upload, so the save resolves as `stopped` and the page,
 * which is gone as well, ignores it.
 */

import { useEffect, useReducer, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { equipmentQueryOptions, type ExerciseDetail } from '~/entities/exercise-library';

import {
  invalidateExerciseLibrary,
  saveExercise,
  type SaveExerciseResult,
} from '../api/exerciseMutations';
import { prepareCover, type PreparedCover } from '~/shared/media/compressCover';
import { coverRefusalKey, type CoverRefusal } from '~/shared/media/coverFile';
import { clampPercent } from '~/shared/lib/percent';
import { prepareVideo, type PreparedVideo } from '~/shared/media/video/prepareVideo';
import { videoRefusalKey, type VideoRefusal } from '~/shared/media/video/videoFile';
import {
  blankDraft,
  draftFromProposal,
  draftFromSource,
  draftOfExercise,
  exerciseDraftReducer,
  type ExerciseDraft,
  type ProposedExerciseFields,
  type DraftVideo,
  type ExerciseDraftAction,
} from '../model/exerciseDraft';
import { createExerciseRequest, editExerciseRequest } from '../model/createRequest';
import {
  chosenEquipmentNames,
  equipmentTiles,
  equipmentReadOf,
  type EquipmentTilesView,
} from '../model/equipmentTiles';
import {
  draftBlockers,
  reachableStep,
  saveMove,
  stepBlockers,
  type DraftBlocker,
} from '../model/draftValidation';
import type { SavePhase } from '../model/saveProgress';
import {
  WIZARD_STEPS,
  nextStep,
  previousStep,
  wizardStepViews,
  type WizardStep,
  type WizardStepView,
} from '../model/wizardStep';

/**
 * A movement for a name outside the catalogue (ai-plan-builder R8): the name as Buddy wrote it,
 * and its proposal, or `null` when Buddy could not propose valid values. Either way the save marks
 * the movement as Buddy's for review.
 */
export interface ProposedNewExercise {
  readonly name: string;
  readonly proposal: ProposedExerciseFields | null;
}

/** Which of the three things the wizard was opened to do. */
export type CreateExerciseMode = 'create' | 'edit' | 'duplicate';

export interface CreateExerciseInput {
  readonly mode: CreateExerciseMode;
  /** The movement being edited or copied. `null` for a fresh one. */
  readonly source: ExerciseDetail | null;
  /** A fresh movement for a name Buddy wrote, filled from its proposal when it made one. */
  readonly proposed?: ProposedNewExercise;
  /** Called once the save has landed AND the catalogue has been re-read. */
  readonly onSaved: (result: SaveExerciseResult) => void;
  /**
   * Told the moment a save starts and when it settles, so the dialog's root — which sits above this
   * hook — can refuse Escape and the scrim while one is in flight.
   */
  readonly onBusyChange: (busy: boolean) => void;
}

export interface CreateExerciseState {
  readonly mode: CreateExerciseMode;
  readonly draft: ExerciseDraft;
  readonly dispatch: (action: ExerciseDraftAction) => void;
  readonly step: WizardStep;
  readonly steps: readonly WizardStepView[];
  readonly goTo: (step: WizardStep) => void;
  readonly back: () => void;
  readonly next: () => void;
  /** True on the last step, where the primary saves rather than advances. */
  readonly onLastStep: boolean;
  /** Everything standing between this draft and a save. Empty when nothing is. */
  readonly blockers: readonly DraftBlocker[];
  /** What stops „Dalej” on the step that is open — its own blockers only (D19). */
  readonly stepBlockers: readonly DraftBlocker[];
  /** The field a refusal wants focus on; the wizard's contents perform it. */
  readonly focusRequest: { readonly blocker: DraftBlocker; readonly n: number } | null;
  /** Save, or take the trainer to the earliest thing that is wrong. */
  readonly submit: () => void;
  /** A save is running: the step is locked and the footer says what it waits for. */
  readonly busy: boolean;
  /** Where the running save is, or `null`. */
  readonly savePhase: SavePhase | null;
  /** Stop a save that is still waiting for a cover or a film: nothing is sent, the draft stays. */
  readonly stopSave: () => void;
  /** A chosen picture is still being re-encoded; a save pressed now waits for it. */
  readonly coverPreparing: boolean;
  /** Why the last picture chosen cannot be the cover, or `null`. */
  readonly coverRefusal: CoverRefusal | null;
  /** Re-encode a chosen picture into the draft's cover, or refuse it. */
  readonly chooseCover: (file: File) => void;
  readonly removeCover: () => void;
  /** A chosen film is being compressed: how far, in whole percent. `null` when none is. */
  readonly videoPreparingPercent: number | null;
  /** Why the last film chosen cannot be sent, and its name, or `null`. */
  readonly videoRefusal: RefusedVideo | null;
  /** Compress a chosen film into the draft's video, or refuse it. */
  readonly chooseVideo: (file: File) => void;
  /** Drop the chosen film, cancelling its compression if it is still running. */
  readonly removeVideo: () => void;
  /** The saved movement's film is uploading: how far, in whole percent. `null` otherwise. */
  readonly videoUploadPercent: number | null;
  /** The film's bytes are up and its confirm is in flight: past the point the film can be stopped. */
  readonly videoConfirming: boolean;
  /** Stop the film's upload. The movement is saved already; the save closes without the film. */
  readonly stopVideoUpload: () => void;
  /** The save's own failure, as a KEY, or `null`. Never the cover's — see `api/exerciseMutations`. */
  readonly saveErrorKey: string | null;
  /** The equipment box: the taxonomy's tiles with the draft's selection marked, or which state. */
  readonly equipment: EquipmentTilesView;
  readonly retryEquipment: () => void;
  readonly equipmentSearch: string;
  readonly setEquipmentSearch: (value: string) => void;
  /** „Jak działają metryki" — open or closed. */
  readonly metricsHelpOpen: boolean;
  readonly toggleMetricsHelp: () => void;
}

/** A film that was refused, and the name the trainer knows it by. */
export interface RefusedVideo {
  readonly refusal: VideoRefusal;
  readonly fileName: string;
}

/** A film being compressed: what the save would wait for, and how far it has got. */
interface VideoPreparing {
  readonly preparation: Promise<PreparedVideo>;
  readonly percent: number;
}

/** The film's part of a running save: its bytes going up, then its confirm. */
type VideoUpload = { readonly percent: number } | 'confirming' | null;

/** A 0-to-1 fraction as the whole percent a bar draws. */
function wholePercent(fraction: number): number {
  return clampPercent(Math.floor(fraction * 100)) ?? 0;
}

function initialDraft(mode: CreateExerciseMode, source: ExerciseDetail | null, proposed: ProposedNewExercise | undefined): ExerciseDraft {
  if (source === null) return proposed === undefined ? blankDraft() : draftFromProposal(proposed.name, proposed.proposal);
  return mode === 'duplicate' ? draftFromSource(source) : draftOfExercise(source);
}

export function useCreateExercise(input: CreateExerciseInput): CreateExerciseState {
  const client = useQueryClient();

  const [draft, rawDispatch] = useReducer(exerciseDraftReducer, null, () =>
    initialDraft(input.mode, input.source, input.proposed),
  );
  const [step, setStep] = useState<WizardStep>('basics');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [metricsHelpOpen, setMetricsHelpOpen] = useState(false);
  const [saveErrorKey, setSaveErrorKey] = useState<string | null>(null);
  /* Set in the press's own handler rather than read off the mutation: React flushes a click's state
   * synchronously, where the mutation's pending state arrives a tick later through the query cache —
   * and an Escape pressed inside that tick closed the dialog over its own running save. */
  const [savePhase, setSavePhase] = useState<SavePhase | null>(null);
  const busy = savePhase !== null;
  const [focusRequest, setFocusRequest] = useState<CreateExerciseState['focusRequest']>(null);
  const [coverPreparation, setCoverPreparation] = useState<Promise<PreparedCover> | null>(null);
  const [coverRefusal, setCoverRefusal] = useState<CoverRefusal | null>(null);
  /* The preparation whose answer may still land. Written only in handlers and in its own
   * settlement: a picture chosen, then replaced or removed before it was ready, must not land. */
  const latestCover = useRef<Promise<PreparedCover> | null>(null);
  const [videoPreparing, setVideoPreparing] = useState<VideoPreparing | null>(null);
  const [videoRefusal, setVideoRefusal] = useState<RefusedVideo | null>(null);
  const [videoUpload, setVideoUpload] = useState<VideoUpload>(null);
  /* The switch of the film preparation whose answer may still land — its identity is the staleness
   * check, as `latestCover`'s is. Written only in handlers, its own settlement and the cleanup. */
  const latestVideo = useRef<AbortController | null>(null);
  /* The running save's two stop switches: its wait for the media, and the film's upload. Written in
   * the save and read in the handlers that stop them and in the unmount cleanup; never in render. */
  const saveWait = useRef<AbortController | null>(null);
  const uploadStop = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      saveWait.current?.abort();
      latestVideo.current?.abort();
      uploadStop.current?.abort();
    },
    [],
  );

  const resetUpload = () => {
    uploadStop.current = null;
    setVideoUpload(null);
  };

  const equipmentQuery = useQuery(equipmentQueryOptions());
  const equipmentRead = equipmentReadOf(equipmentQuery);
  const equipment = equipmentTiles(equipmentRead, equipmentSearch, draft.equipmentIds);
  const chosenNames = chosenEquipmentNames(
    equipmentRead,
    draft.equipmentIds,
    input.source?.equipment ?? [],
  );

  const blockers = draftBlockers(draft);

  /* Every draft change clears a failed save's sentence: it described the draft as it was, and while
   * it stood it hid the blockers the footer would otherwise be naming. */
  const dispatch = (action: ExerciseDraftAction) => {
    setSaveErrorKey(null);
    rawDispatch(action);
  };
  const onLastStep = step === WIZARD_STEPS[WIZARD_STEPS.length - 1];

  const write = useMutation({
    // `waitFor` is the preparation running when save was pressed, or `null`; the body is the draft
    // as it stood at the press, like any save.
    mutationFn: async (waitFor: {
      readonly cover: Promise<PreparedCover> | null;
      readonly video: Promise<PreparedVideo> | null;
    }): Promise<
      | { readonly saved: SaveExerciseResult }
      | { readonly refusedKey: string }
      | { readonly waitStopped: true }
    > => {
      const wait = new AbortController();
      saveWait.current = wait;
      // Settles the moment the trainer stops the wait, whichever preparation is still running.
      const stopped = new Promise<'stopped'>((resolve) => {
        wait.signal.addEventListener('abort', () => {
          resolve('stopped');
        });
      });
      let cover = draft.cover;
      if (waitFor.cover !== null) {
        const prepared = await Promise.race([waitFor.cover, stopped]);
        if (prepared === 'stopped') return { waitStopped: true };
        if (!prepared.ok) return { refusedKey: coverRefusalKey(prepared.refusal) };
        cover = prepared.file;
      }
      let video: DraftVideo | null = draft.video;
      if (waitFor.video !== null) {
        // A rejection is not caught: under a running save only the wizard going away aborts the
        // preparation (see the header), and that stops the wait first.
        const prepared = await Promise.race([waitFor.video, stopped]);
        if (prepared === 'stopped') return { waitStopped: true };
        if (!prepared.ok) return { refusedKey: videoRefusalKey(prepared.refusal) };
        video = { file: prepared.file, durationSeconds: prepared.durationSeconds };
      }
      // Both preparations may have settled in the same turn as the press that stopped the wait.
      if (wait.signal.aborted) return { waitStopped: true };
      saveWait.current = null;
      setSavePhase('writing');
      const stop = new AbortController();
      uploadStop.current = stop;
      const media = {
        cover,
        video,
        onVideoProgress: (fraction: number) => {
          setVideoUpload({ percent: wholePercent(fraction) });
        },
        videoSignal: stop.signal,
        // The PUT has answered, so the stop has nothing left to stop: the control goes with it.
        onVideoSent: () => {
          uploadStop.current = null;
          setVideoUpload('confirming');
        },
      };
      const result = await saveExercise(
        input.mode === 'edit' && input.source !== null
          ? {
              mode: 'edit',
              exerciseId: input.source.id,
              body: editExerciseRequest(draft),
              ...media,
            }
          : { mode: 'create', body: createExerciseRequest(draft), ...media },
      );
      // The film's part is over, and so is the way to stop it; the re-read below is not the film's.
      resetUpload();
      // AWAITED inside the mutation, so the wizard stays busy until the table it is about to
      // uncover has the new row. Resolving earlier closes onto a list that changes a beat later.
      await invalidateExerciseLibrary(client);
      return { saved: result };
    },
    onSuccess: (outcome) => {
      if ('waitStopped' in outcome) {
        // Nothing was written. The draft and whatever is still being prepared stay as they were.
        setSaveErrorKey(null);
        return;
      }
      if ('refusedKey' in outcome) {
        // Nothing was saved. The refusal is said in the footer's alert and stands in the zone;
        // the step with the zone is where the trainer decides whether to save without it.
        setSaveErrorKey(outcome.refusedKey);
        setStep('materials');
        return;
      }
      setSaveErrorKey(null);
      input.onSaved(outcome.saved);
    },
    onError: () => {
      setSaveErrorKey('exerciseLibrary.createWizard.saveErrorBody');
    },
    onSettled: () => {
      saveWait.current = null;
      resetUpload();
      setSavePhase(null);
      input.onBusyChange(false);
    },
  });

  /* A refusal moves focus to the field that fixes it: the wizard's contents answer `focusRequest`
   * after the step it names has rendered. A counter makes the same blocker twice two requests. */
  const refuse = (target: WizardStep, blocker: DraftBlocker) => {
    setStep(target);
    setFocusRequest((previous) => ({ blocker, n: (previous?.n ?? 0) + 1 }));
  };

  const submit = () => {
    // Cleared first, so a second failure is a fresh insert into the always-mounted alert rather
    // than the same text it already holds — which no screen reader announces twice.
    setSaveErrorKey(null);
    const move = saveMove(draft, {
      cover: coverPreparation !== null,
      video: videoPreparing !== null,
    });
    if (move.kind === 'refuse') {
      refuse(move.step, move.blocker);
      return;
    }
    setSavePhase(move.kind === 'waitForMedia' ? 'waitingForMedia' : 'writing');
    input.onBusyChange(true);
    write.mutate({ cover: coverPreparation, video: videoPreparing?.preparation ?? null });
  };

  const chooseCover = (file: File) => {
    if (busy) return;
    const preparation = prepareCover(file);
    latestCover.current = preparation;
    setCoverRefusal(null);
    setCoverPreparation(preparation);
    void preparation.then((prepared) => {
      if (latestCover.current !== preparation) return;
      latestCover.current = null;
      setCoverPreparation(null);
      if (prepared.ok) dispatch({ type: 'setCover', file: prepared.file });
      else setCoverRefusal(prepared.refusal);
    });
  };

  /* Also clears a failed save's sentence, as every draft change does (`dispatch`): the next film's
   * refusal is spoken by the live region only while no save failure stands (`model/saveProgress`),
   * and choosing a film does not always dispatch — after a refusal there is no film to drop. */
  const forgetVideoPreparation = () => {
    setSaveErrorKey(null);
    latestVideo.current?.abort();
    latestVideo.current = null;
    setVideoPreparing(null);
    setVideoRefusal(null);
  };

  const chooseVideo = (file: File) => {
    if (busy) return;
    forgetVideoPreparation();
    // The new film replaces the chosen one from this moment, whatever becomes of it.
    if (draft.video !== null) dispatch({ type: 'setVideo', video: null });
    const abort = new AbortController();
    const preparation = prepareVideo(file, {
      signal: abort.signal,
      onProgress: (fraction) => {
        setVideoPreparing((current) =>
          current?.preparation === preparation
            ? { preparation, percent: wholePercent(fraction) }
            : current,
        );
      },
    });
    latestVideo.current = abort;
    setVideoPreparing({ preparation, percent: 0 });
    preparation.then(
      (prepared) => {
        if (latestVideo.current !== abort) return;
        latestVideo.current = null;
        setVideoPreparing(null);
        if (prepared.ok) {
          dispatch({
            type: 'setVideo',
            video: { file: prepared.file, durationSeconds: prepared.durationSeconds },
          });
        } else setVideoRefusal({ refusal: prepared.refusal, fileName: file.name });
      },
      // Aborted: replaced, removed or closed over. Whoever aborted it has already moved on.
      () => undefined,
    );
  };

  return {
    mode: input.mode,
    draft,
    dispatch,
    step,
    steps: wizardStepViews(draft, step, chosenNames),
    // Never past a blocker (D19): a jump lands on the first earlier step that is not done.
    goTo: (target) => {
      const land = reachableStep(draft, target);
      const first = stepBlockers(draft, land)[0];
      if (land !== target && first !== undefined) refuse(land, first);
      else setStep(target);
    },
    back: () => {
      const target = previousStep(step);
      if (target !== null) setStep(target);
    },
    // „Dalej” refuses while THIS step has its own blockers, and takes focus to the first of them.
    next: () => {
      const own = stepBlockers(draft, step)[0];
      if (own !== undefined) {
        refuse(step, own);
        return;
      }
      const target = nextStep(step);
      if (target !== null) setStep(target);
    },
    onLastStep,
    blockers,
    stepBlockers: stepBlockers(draft, step),
    focusRequest,
    submit,
    busy,
    savePhase,
    stopSave: () => {
      saveWait.current?.abort();
    },
    coverPreparing: coverPreparation !== null,
    coverRefusal,
    chooseCover,
    removeCover: () => {
      if (busy) return;
      latestCover.current = null;
      setCoverPreparation(null);
      setCoverRefusal(null);
      dispatch({ type: 'setCover', file: null });
    },
    videoPreparingPercent: videoPreparing?.percent ?? null,
    videoRefusal,
    chooseVideo,
    removeVideo: () => {
      if (busy) return;
      forgetVideoPreparation();
      dispatch({ type: 'setVideo', video: null });
    },
    videoUploadPercent:
      videoUpload !== null && videoUpload !== 'confirming' ? videoUpload.percent : null,
    videoConfirming: videoUpload === 'confirming',
    stopVideoUpload: () => {
      uploadStop.current?.abort();
    },
    saveErrorKey,
    equipment,
    retryEquipment: () => {
      void equipmentQuery.refetch();
    },
    equipmentSearch,
    setEquipmentSearch,
    metricsHelpOpen,
    toggleMetricsHelp: () => {
      setMetricsHelpOpen((open) => !open);
    },
  };
}
